import { useEffect, useMemo, useState } from 'react';

import produce from 'immer';

import { useQueryState } from 'hooks/useQueryState';

interface Props {
    dropdownFiltersKeys: string[];
}
interface FilterOption {
    [key: string]: { label: string; isSelected: boolean };
}

const SelectedFiltersOptions: FilterOption = {
    milestoneFragnet: { label: 'Activity Fragnet', isSelected: false },
    activityForwardFragnet: { label: 'Activity Forward Fragnet', isSelected: false },
    baselineStartDate: { label: 'Baseline Start Date', isSelected: false },
    baselineFinishDate: { label: 'Baseline Finish Date', isSelected: false },
    cardType: { label: 'Card Type', isSelected: false },
    cluster: { label: 'Cluster', isSelected: false },
    completionPlanSegment: { label: 'Completion Plan Segment', isSelected: false },
    duration: { label: 'Duration', isSelected: false },
    float: { label: 'Float', isSelected: false },
    hasAttachments: { label: 'Has Attachments', isSelected: false },
    hasComments: { label: 'Has Comments', isSelected: false },
    lagLead: { label: 'Lag/Lead', isSelected: false },
    relatedActivity: { label: 'Related Activity', isSelected: false },
    resourceName: { label: 'Resource Name', isSelected: false },
    resourceType: { label: 'Resource Type', isSelected: false },
    startDate: { label: 'Start Date', isSelected: false },
    submittedCompletion: { label: 'Submitted Completion', isSelected: false },
    trackedActivity: { label: 'Tracked Activity', isSelected: false },
    updatedBy: { label: 'Updated By', isSelected: false },
    wbsLevel: { label: 'WBS Level', isSelected: false },
};

const getInitialDropdownFiltersOptions = (dropdownFiltersKeys: string[]): FilterOption => {
    return dropdownFiltersKeys.reduce<FilterOption>((acc, key) => {
        if (SelectedFiltersOptions[key]) {
            acc[key] = { ...SelectedFiltersOptions[key] };
        }
        return acc;
    }, {});
};

export const useGetDropdownOptions = ({ dropdownFiltersKeys }: Props) => {
    const initialDropdownFiltersOptions = useMemo(
        () => getInitialDropdownFiltersOptions(dropdownFiltersKeys),
        [dropdownFiltersKeys],
    );
    const [dropdownFiltersOptions, setDropdownFiltersOptions] = useState<FilterOption>(initialDropdownFiltersOptions);

    // Query state hooks
    const [qActivityFragnet] = useQueryState('milestoneFragnet');
    const [qActivityForwardFragnet] = useQueryState('activityForwardFragnet');
    const [qCardActivity] = useQueryState('cardActivity');
    const [qAssignee] = useQueryState('assignee');
    const [qStatus] = useQueryState('status');
    const [qRiskIndicators] = useQueryState('riskIndicators');
    const [qImportant] = useQueryState('important');
    const [qUrgent] = useQueryState('urgent');
    const [qIsAdvancedTextSearch] = useQueryState('isAdvancedTextSearch');
    const [qEndDatesRange] = useQueryState('endDatesRange');
    const [qStartDatesRange] = useQueryState('startDatesRange');
    const [qCommentRange] = useQueryState('commentRange');
    const [qBaselineStartDatesRange] = useQueryState('baselineStartDatesRange');
    const [qBaselineEndDatesRange] = useQueryState('baselineEndDatesRange');
    const [qFinishDate] = useQueryState('finishDate');
    const [qActivityCodes] = useQueryState('activityCodes');
    const [qExcludeActivityCodes] = useQueryState('excludeActivityCodes');
    const [qWbs] = useQueryState('wbs');
    const [qCardType] = useQueryState('cardType');
    const [qCompletionPlanSegment] = useQueryState('completionPlanSegment');
    const [qDuration] = useQueryState('duration');
    const [qFloat] = useQueryState('float');
    const [qHasAttachments] = useQueryState('hasAttachments');
    const [qHasComments] = useQueryState('hasComments');
    const [qLagLead] = useQueryState('lagLead');
    const [qRelatedActivity] = useQueryState('relatedActivity');
    const [qResourceType] = useQueryState('resourceType');
    const [qStartDate] = useQueryState('startDate');
    const [qSubmittedCompletion] = useQueryState('submittedCompletion');
    const [qTrackedActivity] = useQueryState('trackedActivity');
    const [qUpdatedBy] = useQueryState('updatedBy');
    const [qCommenter] = useQueryState('commenter');
    const [qMentioned] = useQueryState('mentioned');
    const [qWbsLevel] = useQueryState('wbsLevel');
    const [qResourceName] = useQueryState('resourceName');
    const [qBaselineStartDate] = useQueryState('baselineStartDate');
    const [qBaselineEndDate] = useQueryState('baselineEndDate');
    const [qCluster] = useQueryState('cluster');

    // Map of query key names to their query state values for dynamic checking
    const queryStateMap = useMemo<Record<string, unknown>>(
        () => ({
            milestoneFragnet: qActivityFragnet,
            activityForwardFragnet: qActivityForwardFragnet,
            cardActivity: qCardActivity,
            assignee: qAssignee,
            status: qStatus,
            riskIndicators: qRiskIndicators,
            important: qImportant,
            urgent: qUrgent,
            isAdvancedTextSearch: qIsAdvancedTextSearch,
            endDatesRange: qEndDatesRange,
            startDatesRange: qStartDatesRange,
            commentRange: qCommentRange,
            baselineStartDatesRange: qBaselineStartDatesRange,
            baselineEndDatesRange: qBaselineEndDatesRange,
            finishDate: qFinishDate,
            activityCodes: qActivityCodes,
            excludeActivityCodes: qExcludeActivityCodes,
            wbs: qWbs,
            cardType: qCardType,
            completionPlanSegment: qCompletionPlanSegment,
            duration: qDuration,
            float: qFloat,
            hasAttachments: qHasAttachments,
            hasComments: qHasComments,
            lagLead: qLagLead,
            relatedActivity: qRelatedActivity,
            resourceType: qResourceType,
            startDate: qStartDate,
            submittedCompletion: qSubmittedCompletion,
            trackedActivity: qTrackedActivity,
            updatedBy: qUpdatedBy,
            commenter: qCommenter,
            mentioned: qMentioned,
            wbsLevel: qWbsLevel,
            resourceName: qResourceName,
            baselineStartDate: qBaselineStartDate,
            baselineEndDate: qBaselineEndDate,
            cluster: qCluster,
        }),
        [
            qActivityFragnet,
            qActivityForwardFragnet,
            qActivityCodes,
            qAssignee,
            qBaselineEndDate,
            qBaselineEndDatesRange,
            qBaselineStartDate,
            qBaselineStartDatesRange,
            qCardActivity,
            qCardType,
            qCluster,
            qCommentRange,
            qCommenter,
            qCompletionPlanSegment,
            qDuration,
            qEndDatesRange,
            qExcludeActivityCodes,
            qFinishDate,
            qFloat,
            qHasAttachments,
            qHasComments,
            qImportant,
            qIsAdvancedTextSearch,
            qLagLead,
            qMentioned,
            qRelatedActivity,
            qResourceName,
            qResourceType,
            qRiskIndicators,
            qStartDate,
            qStartDatesRange,
            qStatus,
            qSubmittedCompletion,
            qTrackedActivity,
            qUpdatedBy,
            qUrgent,
            qWbs,
            qWbsLevel,
        ],
    );

    /**
     * Initialize customize filter options based on query params
     * Determines if there are any query parameters present in the URL that correspond to the keys in the CustomizeFilterOptions object.
     * If a match is found, the respective filter option's isSelected property is set to true and the filter will be visible to the user.
     * Finally, it marks the customization options as initialized to prevent redundant executions.
     */
    useEffect(() => {
        setDropdownFiltersOptions((prev) =>
            produce(prev ?? initialDropdownFiltersOptions, (draft) => {
                Object.keys(draft).forEach((item) => {
                    if (queryStateMap[item]) {
                        draft[item].isSelected = true;
                    }
                });
                if (qStartDatesRange && draft.startDate) {
                    draft.startDate.isSelected = true;
                }
                if (qBaselineStartDatesRange && draft.baselineStartDate) {
                    draft.baselineStartDate.isSelected = true;
                }
                if (qBaselineEndDatesRange && draft.baselineFinishDate) {
                    draft.baselineFinishDate.isSelected = true;
                }
                if (qCommentRange && draft.hasComments) {
                    draft.hasComments.isSelected = true;
                }
            }),
        );
    }, [
        initialDropdownFiltersOptions,
        qBaselineEndDatesRange,
        qBaselineStartDatesRange,
        qCommentRange,
        qStartDatesRange,
        queryStateMap,
    ]);

    return {
        dropdownFiltersOptions,
        setDropdownFiltersOptions,
    };
};
